import Link from 'next/link';
import { PageTitle } from '../PageTitle';
import Image from 'next/image';
import { Paths, PathsForExternal } from '@/modules/Paths';
import { useLocale, useTranslate } from 'app/[locale]/hooks/useTranslate';
import { ArrowRightIcon } from 'app/[locale]/(homepage)/(home)/integrations/components/ArrowRightIcon';
import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';
import { useAttentionTracker } from 'app/[locale]/hooks/useAttentionTracker';

export const MainHeader = (): JSX.Element => {
  const locale = useLocale();
  const { t } = useTranslate();
  const { ref } = useAttentionTracker({ sectionName: 'main_header' });

  return (
    <header className="max-w-homepage px-4 md:px-10" ref={ref}>
      <PageTitle
        title={t.home.MAIN_PAGE_TITLE}
        description={t.home.MAIN_PAGE_DESC}
        className="gap-6"
        size={'lg'}
      />
      <article className="flex justify-center items-center gap-2 select-text mt-8 md:mt-10 animate-[riseFast_1s_ease-in-out]">
        <p className="text-xs md:text-sm text-black-dark">{t.home.MAIN_HEADER_TOOLS_LABEL}</p>
        {integrations.map(({ src, alt }) => (
          <Image key={alt} src={src} width={24} height={24} className="shrink-0" alt={alt} />
        ))}
      </article>
      <div className="w-fit grid grid-cols-1 md:grid-cols-2 items-center justify-center gap-2 mt-8 md:gap-5 mx-auto *:whitespace-nowrap animate-[riseFast_1.2s_ease-in-out]">
        <Link href={Paths.Login} passHref>
          <button
            onMouseDown={() => {
              analyticsInstance.logEvent('click_try_free_trial', {
                page: '/',
                location: 'page_header',
              });
            }}
            className="w-full flex justify-center items-center gap-2 transition-all duration-[0.2s] ease-in-out bg-navy-cool hover:bg-primary hover:shadow rounded py-4 px-8 md:py-5 md:px-6 text-white font-semibold text-base md:text-lg"
          >
            {t.home.HEADER_TRY_TRIAL}

            <ArrowRightIcon className="w-4 h-4 md:w-5 md:h-5 text-white" />
          </button>
        </Link>
        <Link
          href={
            locale === 'ja'
              ? PathsForExternal.IntroductionDocsForJA
              : PathsForExternal.IntroductionDocsForKO
          }
          target="_blank"
          passHref
        >
          <button
            onMouseDown={() => {
              analyticsInstance.logEvent('click_view_introduction_docs');
            }}
            className="w-full text-base md:text-lg font-semibold text-center bg-white text-black-light border border-solid border-gray-soft rounded py-4 px-8 md:py-5 md:px-6"
          >
            {t.home.HEADER_INTRODUCTION_DOCS}
          </button>
        </Link>
      </div>
    </header>
  );
};

const integrations = [
  { src: '/callabo/integration/google_meet.svg', alt: 'google meet' },
  { src: '/callabo/integration/teams.svg', alt: 'teams' },
  { src: '/callabo/integration/zoom.svg', alt: 'zoom' },
  { src: '/callabo/integration/webex.svg', alt: 'webex' },
];
